import { WarningTriangleIcon } from "@xola/icons";
import { Dot } from "@xola/ui-kit";
import clsx from "clsx";
import React, { memo, ReactElement, ReactNode } from "react";
import { Event, EventGuide, EventGuideStatus } from "types";
import { GuideList } from "./Guides/GuideList";
import { InventoryList } from "./InventoryList";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardMode } from "@/types/Dashboard";

interface ResourceDisplayProps {
    icon: ReactElement;

    color?: string;

    children?: ReactNode;

    shouldShowDot?: boolean;

    className?: string;
}

export const ResourceDisplay = memo(
    ({ color, icon, children, shouldShowDot = false, className }: ResourceDisplayProps) => {
        const mode = useDashboardFilterStore((state) => state.filters.mode);
        const isCompact = mode !== DashboardMode.LIST && mode !== DashboardMode.DAY;

        return (
            <div className={clsx("ui-resource-display flex items-center", className)}>
                {shouldShowDot && (
                    <span className="flex">
                        <Dot color={color} className={clsx(isCompact ? "mr-0.5" : "mr-1")} />
                    </span>
                )}
                <span className={clsx(isCompact ? "mr-1" : "mr-1.5")}>{icon}</span>
                <span className="align-middle hover:underline" data-testid="resource-display">
                    {children}
                </span>
            </div>
        );
    },
);

export enum DashboardResourceType {
    RESOURCE = "resourceUsages",
    GUIDES = "guides",
}

interface ResourceDisplayElementProps {
    event: Event | undefined;
    isCompact: boolean;
    type: DashboardResourceType;
}

export const ResourceDisplayElement = ({ event, type, isCompact = true }: ResourceDisplayElementProps) => {
    const isInventory = type === DashboardResourceType.RESOURCE;
    let needsResourceAssignment = event ? event[type].length === 0 : true;
    if (event && isInventory) {
        // Event does not need inventory assignment if the count of resource is reduced to zero
        needsResourceAssignment = event[type].every((resource) => resource.count === 0);
    }

    if (needsResourceAssignment) {
        return <div className="ui-assign-resource w-fit hover:cursor-pointer hover:underline">+ Assign</div>;
    }

    if (!event) {
        return null;
    }

    const ResourceType = isInventory ? InventoryList : GuideList;
    return (
        <div className="flex w-full items-center">
            <ResourceType event={event} showOnlyCount={isCompact} />
        </div>
    );
};

export const ResourceIcon = ({
    event,
    type,
    icon,
}: {
    event?: Event;
    type: DashboardResourceType;
    icon: ReactElement;
}) => {
    if (event && type === DashboardResourceType.GUIDES) {
        const hasDeclined = getMostImportantGuideStatus(event.guides) === EventGuideStatus.DECLINED;
        return hasDeclined ? <WarningTriangleIcon className="text-red" /> : icon;
    }

    return icon;
};

export enum DisplayType {
    FULL = "full",
    COMPACT = "compact",
}

const getMostImportantGuideStatus = (guides: EventGuide[]) => {
    const statuses = new Set(guides.map((guide) => guide.status));

    if (statuses.has(EventGuideStatus.DECLINED)) {
        return EventGuideStatus.DECLINED;
    }

    if (statuses.has(EventGuideStatus.PENDING)) {
        return EventGuideStatus.PENDING;
    }

    return EventGuideStatus.CONFIRMED;
};

export const getColor = (event: Event | null, needsResourceAssignment: boolean) => {
    if (event) {
        // Check if the guide is pending, if yes we need to show a caution dot, if guide is declined - show danger
        switch (getMostImportantGuideStatus(event.guides)) {
            case EventGuideStatus.DECLINED:
                return "danger";
            case EventGuideStatus.PENDING:
                return "caution";
            default:
                return needsResourceAssignment ? "danger" : "success";
        }
    }

    return needsResourceAssignment ? "danger" : "success";
};
